import React, { useState } from "react";
import { postRequestWithoutToken, saveUserToken } from "../Utility/Utility";
import "./LoginPage.css";

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !pin) {
      setError("Please enter both email and PIN");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }

    try {
      const payload = { email, pin };

      const response = await postRequestWithoutToken(
        "biostarapp/user/client/login",
        payload,
      );

      console.log("login response:", response);

      if (response?.status === true) {
        // Save token using utility function
        const token = response?.tokendata?.token || response?.data?.token;
        saveUserToken(token);
        onLogin(response.data);
      } else {
        setError(response?.msg || "Login failed");
      }
    } catch (err) {
      console.error("login error:", err);

      setError(
        err?.response?.data?.msg || "Unable to login. Please try again.",
      );
    }
  };

  return (
    <div className={`login-container ${mounted ? "mounted" : ""}`}>
      {/* Background orbs */}
      <div className="background-orbs">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="orb-grid" />
      </div>

      <div className="login-content">
        {/* Header */}
        <header className="login-header">
          <div className="logo-container">
            <div className="logo-icon">
              {[36, 46, 56].map((s, i) => (
                <span key={i} className={`ring-ring ring-${i}`} />
              ))}
              <svg
                className="logo-svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#60a5fa"
                strokeWidth="1.5"
              >
                <path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2" />
                <path d="M12 19v3" />
              </svg>
            </div>
            <h1 className="logo-text">VoxPlayer</h1>
            <span className="logo-badge">Pro</span>
          </div>
          <p className="logo-subtitle">Next-Gen Voice Capture</p>
        </header>

        {/* Login Card */}
        <div className="login-card-wrapper">
          <div className="login-card-header">
            <span className="pulse-dot" />
            <span className="secure-text">Secure Access</span>
          </div>

          <div className="login-card">
            <form onSubmit={handleSubmit}>
              {/* Email Input */}
              <div className="input-group">
                <label className="input-label">Email Address</label>
                <div className="input-wrapper">
                  <svg
                    className="input-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="1.5"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="email-input"
                  />
                </div>
              </div>

              {/* PIN Input */}
              {/* PIN Input */}
              <div className="input-group">
                <label className="input-label">PIN</label>

                <div className="input-wrapper">
                  <svg
                    className="input-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="1.5"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>

                  <input
                    type={showPin ? "text" : "password"}
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter your PIN"
                    maxLength="6"
                    className="pin-input"
                  />

                  {/* Eye Toggle */}
                  <button
                    type="button"
                    className="eye-button"
                    onClick={() => setShowPin(!showPin)}
                  >
                    {showPin ? (
                      /* Eye Open */
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#64748b"
                        strokeWidth="1.5"
                      >
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      /* Eye Closed */
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#64748b"
                        strokeWidth="1.5"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0112 19c-7 0-11-7-11-7a21.77 21.77 0 015.17-5.94" />
                        <path d="M1 1l22 22" />
                        <path d="M9.9 4.24A10.94 10.94 0 0112 5c7 0 11 7 11 7a21.77 21.77 0 01-2.88 4.2" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#f43f5e"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M15 9l-6 6M9 9l6 6" />
                  </svg>
                  <span className="error-text">{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button type="submit" className="submit-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                >
                  <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M13.8 12H3" />
                </svg>
                Access VoxPlayer
              </button>
            </form>
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <svg
            className="info-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <p className="info-text">
            Enter your registered email and secure PIN to access the voice
            recording platform.
          </p>
        </div>

        {/* Footer */}
        <footer className="login-footer">
          <div className="footer-status">
            <span className="status-dot" />
            Secure Connection
          </div>
          <span className="footer-copyright">Powered by Biostar</span>
        </footer>

        {/* Footer */}
        <footer className="login-footer-update">
          <div className="footer-status">
            <span
              className="footer-copyright"
              onClick={() => setShowPrivacy(true)}
              style={{ cursor: "pointer" }}
            >
              Privacy Policy
            </span>
          </div>
        </footer>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="modal-overlay" onClick={() => setShowPrivacy(false)}>
          <div className="privacy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Privacy Policy</h2>
              <button
                className="modal-close"
                onClick={() => setShowPrivacy(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-content">
              <p>
                <strong>Last Updated: 11 February 2026</strong>
              </p>

              <p>
                This Privacy Policy explains how{" "}
                <strong>Biostar VoxPlayer</strong> ("we", "our", or "us")
                collects, uses, processes, and protects your Personal Data when
                you use our website, applications, and voice-based services
                (collectively, the “Services”).
              </p>

              <p>
                <strong>Personal Data</strong> refers to any information that
                identifies or may identify you, including your name, email
                address, phone number, IP address, device information, and voice
                recordings.
              </p>

              <p>
                <strong>Information We Collect:</strong>
                <br />
                • Account & Contact Details (name, email, phone number)
                <br />
                • Voice Data & Audio Recordings uploaded to Biostar VoxPlayer
                <br />
                • Device & Usage Information (IP address, browser, operating
                system)
                <br />
                • Payment Information (processed securely through third-party
                providers)
                <br />• Communications, inquiries, and feedback
              </p>

              <p>
                <strong>Voice & AI Processing:</strong>
                <br />
                Biostar VoxPlayer uses AI-powered voice technologies to analyze
                uploaded audio for services including voice modeling, speech
                synthesis, dubbing, and translation. Voice data may be treated
                as biometric information under applicable laws.
              </p>

              <p>
                <strong>How We Use Your Data:</strong>
                <br />
                • To operate and enhance Biostar VoxPlayer Services
                <br />
                • To generate and manage voice models
                <br />
                • For security, fraud prevention, and legal compliance
                <br />
                • For billing and payment processing
                <br />• For research and improvement of AI systems
              </p>

              <p>
                <strong>Legal Basis:</strong>
                <br />
                Personal Data is processed based on contractual necessity,
                legitimate interests, legal obligations, or user consent where
                applicable.
              </p>

              <p>
                <strong>Data Sharing:</strong>
                <br />
                We may share data with trusted partners such as cloud hosting
                providers, analytics services, and payment processors solely to
                support Biostar VoxPlayer operations. We never sell your
                Personal Data.
              </p>

              <p>
                <strong>International Transfers:</strong>
                <br />
                Your data may be stored or processed in the United States or
                other countries where our service providers operate. Appropriate
                safeguards are applied in accordance with applicable laws.
              </p>

              <p>
                <strong>Data Retention:</strong>
                <br />
                Personal Data is retained only as long as necessary to provide
                Services or meet legal requirements. Voice-related data is
                deleted within three (3) years of your last interaction unless
                legally required otherwise.
              </p>

              <p>
                <strong>Your Rights:</strong>
                <br />
                Subject to local laws, you may request:
                <br />
                • Access or correction of your data
                <br />
                • Deletion or anonymization
                <br />
                • Withdrawal of consent
                <br />
                • Objection to processing
                <br />• Data portability
              </p>

              <p>
                <strong>Cookies:</strong>
                <br />
                Biostar VoxPlayer uses cookies for core functionality,
                analytics, and marketing. Cookie preferences can be managed via
                your browser settings.
              </p>

              <p>
                <strong>Children’s Privacy:</strong>
                <br />
                Biostar VoxPlayer Services are not intended for users under 18
                years of age. We do not knowingly collect children’s Personal
                Data.
              </p>

              <p>
                <strong>Security:</strong>
                <br />
                We apply industry-standard security measures to safeguard
                Personal Data, but no system can guarantee absolute protection.
              </p>

              <p>
                <strong>Contact Us:</strong>
                <br />
                For privacy inquiries, please contact Biostar VoxPlayer’s Data
                Protection Officer:
                <br />
                {/* <strong>legal@biostarvoxplayer.com</strong> */}
              </p>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}
